import { CommonModule } from '@angular/common';
import {
  AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy, ViewChild,
} from '@angular/core';

interface Star { x: number; y: number; duration: string; delay: string; big: boolean; }

interface Particle {
  kind: 'smoke' | 'fire';
  x: number; y: number; vx: number; vy: number;
  life: number; max: number; r0: number; r1: number; a0: number;
}

const ROAD_PERIOD = 440;     // px do padrão repetido da pista (faixa + olhos-de-gato)
const IDLE_SPEED = 5.5;      // px/frame em repouso
const LAUNCH_SPEED = 26;     // px/frame durante a largada
const CAR_REF_WIDTH = 780;   // largura em que as partículas foram calibradas

/** Ponto do gradiente de cor do fogo conforme a idade da partícula. */
function fireColor(t: number): string {
  if (t < .3) return '255,243,176';
  if (t < .55) return '255,168,60';
  if (t < .8) return '230,92,0';
  return '190,40,10';
}

/** Silhueta de montanha determinística (seed fixa) duplicada para o loop de parallax ser contínuo. */
function ridgePath(seed: number, segments: number, minH: number, maxH: number): string {
  let s = seed;
  const rnd = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
  const W = 1000, H = 100;
  const pts: [number, number][] = [];
  for (let i = 0; i < segments; i++) {
    pts.push([(i / segments) * W, H - (minH + rnd() * (maxH - minH))]);
  }
  const all = pts.concat(pts.map(p => [p[0] + W, p[1]] as [number, number]));
  const line = all.map(p => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' L');
  return `M0 ${H} L${line} L${2 * W} ${pts[0][1].toFixed(1)} L${2 * W} ${H} Z`;
}

function makeStars(count: number): Star[] {
  const stars: Star[] = [];
  for (let i = 0; i < count; i++) {
    stars.push({
      x: Math.random() * 100,
      y: Math.pow(Math.random(), 1.6) * 100,
      duration: (3 + Math.random() * 5).toFixed(2) + 's',
      delay: (-Math.random() * 8).toFixed(2) + 's',
      big: Math.random() < .2,
    });
  }
  return stars;
}

/**
 * Cena animada da tela de login: estrada 3D, montanhas em parallax, carro com
 * fumaça/backfire em canvas e a "largada" disparada pelo submit do formulário.
 * O loop de animação roda fora do NgZone para não disparar change detection a cada frame.
 */
@Component({
  selector: 'app-login-scene',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './login-scene.component.html',
  styleUrl: './login-scene.component.css',
})
export class LoginSceneComponent implements AfterViewInit, OnDestroy {
  @ViewChild('scene', { static: true }) private sceneRef!: ElementRef<HTMLDivElement>;
  @ViewChild('road', { static: true }) private roadRef!: ElementRef<HTMLDivElement>;
  @ViewChild('carImg', { static: true }) private carImgRef!: ElementRef<HTMLImageElement>;
  @ViewChild('fx', { static: true }) private fxRef!: ElementRef<HTMLCanvasElement>;

  readonly stars = makeStars(70);
  readonly ridgeFar = ridgePath(17, 26, 20, 92);
  readonly ridgeNear = ridgePath(41, 34, 10, 70);

  private readonly reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  private ctx!: CanvasRenderingContext2D;
  private particles: Particle[] = [];
  private width = 0;
  private height = 0;
  private roadY = 0;
  private speed = this.reduceMotion ? 0 : IDLE_SPEED;
  private targetSpeed = this.speed;
  private smokeAcc = 0;
  private rafId = 0;
  private timers = new Set<number>();
  private launchTimer = 0;
  private popping = false;
  private launching = false;

  constructor(private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.ctx = this.fxRef.nativeElement.getContext('2d')!;
    this.resize();
    this.zone.runOutsideAngular(() => { this.rafId = requestAnimationFrame(this.frame); });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.rafId);
    this.timers.forEach(id => clearTimeout(id));
    this.timers.clear();
  }

  @HostListener('window:resize')
  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const scene = this.sceneRef.nativeElement;
    this.width = scene.clientWidth;
    this.height = scene.clientHeight;
    const canvas = this.fxRef.nativeElement;
    canvas.width = this.width * dpr;
    canvas.height = this.height * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /** Backfire: estouros de chama no escapamento com freio aceso. */
  pop(times = 3): void {
    if (this.popping) return;
    this.popping = true;
    const scene = this.sceneRef.nativeElement;
    scene.classList.add('brake');
    let n = 0;
    const one = () => {
      scene.classList.remove('pop');
      void scene.offsetWidth; // reinicia a animação do clarão
      scene.classList.add('pop');
      this.fire(38 + Math.random() * 14);
      this.smoke(8, true);
      if (++n < times) {
        this.after(140 + Math.random() * 90, one);
      } else {
        this.after(420, () => { scene.classList.remove('pop', 'brake'); this.popping = false; });
      }
    };
    one();
  }

  /** Largada: o carro acelera pela pista e some atrás do card. */
  launch(): void {
    if (this.launching) return;
    this.launching = true;
    const scene = this.sceneRef.nativeElement;
    scene.classList.remove('returning');
    this.pop(2);
    this.targetSpeed = this.reduceMotion ? 0 : LAUNCH_SPEED;
    this.launchTimer = this.after(120, () => scene.classList.add('launch'));
  }

  /** Traz o carro de volta à pista (login não concluído). Sem efeito se não houve largada. */
  reset(): void {
    if (!this.launching) return;
    clearTimeout(this.launchTimer);
    this.timers.delete(this.launchTimer);
    const scene = this.sceneRef.nativeElement;
    scene.classList.remove('launch');
    scene.classList.add('returning');
    this.targetSpeed = this.reduceMotion ? 0 : IDLE_SPEED;
    this.after(1300, () => { scene.classList.remove('returning'); this.launching = false; });
  }

  private after(ms: number, fn: () => void): number {
    const id = window.setTimeout(() => { this.timers.delete(id); fn(); }, ms);
    this.timers.add(id);
    return id;
  }

  /** Posição do escapamento na tela (relativa à cena) e fator de escala do carro. */
  private exhaust(): { x: number; y: number; k: number } {
    const r = this.carImgRef.nativeElement.getBoundingClientRect();
    const s = this.sceneRef.nativeElement.getBoundingClientRect();
    return { x: r.left - s.left + r.width * .61, y: r.top - s.top + r.height * .655, k: r.width / CAR_REF_WIDTH };
  }

  private smoke(n: number, big: boolean): void {
    const e = this.exhaust();
    for (let i = 0; i < n; i++) {
      const a = (Math.random() - .5) * .9;
      const sp = (big ? 1.4 : .5) + Math.random() * .6;
      this.particles.push({
        kind: 'smoke',
        x: e.x + (Math.random() - .5) * 8 * e.k, y: e.y,
        vx: Math.cos(a) * sp * .9 * e.k, vy: -Math.sin(a) * sp * .3 - .45 - Math.random() * .4,
        life: 0, max: (big ? 95 : 100) + Math.random() * 50,
        r0: (big ? 8 : 4) * e.k, r1: (big ? 40 : 20) * e.k, a0: big ? .32 : .14,
      });
    }
  }

  private fire(n: number): void {
    const e = this.exhaust();
    for (let i = 0; i < n; i++) {
      const a = (Math.random() - .5) * 1.1 + .25;
      const sp = 1.8 + Math.random() * 5;
      this.particles.push({
        kind: 'fire',
        x: e.x, y: e.y,
        vx: Math.cos(a) * sp * e.k, vy: Math.sin(a) * sp * .55 * e.k - .6,
        life: 0, max: 16 + Math.random() * 22,
        r0: (3 + Math.random() * 4) * e.k, r1: (10 + Math.random() * 8) * e.k, a0: .95,
      });
    }
  }

  private frame = (): void => {
    // pista: acelera/desacelera suavemente até a velocidade alvo
    this.speed += (this.targetSpeed - this.speed) * (this.targetSpeed > this.speed ? .045 : .03);
    this.roadY = (this.roadY + this.speed) % ROAD_PERIOD;
    this.roadRef.nativeElement.style.setProperty('--road-y', this.roadY.toFixed(2) + 'px');

    if (!this.reduceMotion && ++this.smokeAcc >= 8) {
      this.smokeAcc = 0;
      this.smoke(1, false);
    }

    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.width, this.height);
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      const t = ++p.life / p.max;
      if (t >= 1) { this.particles.splice(i, 1); continue; }
      p.x += p.vx;
      p.y += p.vy;
      const r = p.r0 + (p.r1 - p.r0) * t;
      const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
      if (p.kind === 'smoke') {
        p.vx *= .985;
        p.vy -= .006;
        p.x += Math.sin(p.life * .08 + p.max) * .25;
        const a = p.a0 * (1 - t) * Math.min(1, t * 8);
        g.addColorStop(0, `rgba(205,210,220,${a.toFixed(3)})`);
        g.addColorStop(1, 'rgba(205,210,220,0)');
        ctx.globalCompositeOperation = 'source-over';
      } else {
        p.vx *= .92;
        p.vy = p.vy * .92 - .05;
        const c = fireColor(t);
        const a = p.a0 * (1 - t);
        g.addColorStop(0, `rgba(${c},${a.toFixed(3)})`);
        g.addColorStop(.5, `rgba(${c},${(a * .5).toFixed(3)})`);
        g.addColorStop(1, `rgba(${c},0)`);
        ctx.globalCompositeOperation = 'lighter';
      }
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
    this.rafId = requestAnimationFrame(this.frame);
  };
}
